import type { CSSProperties } from "react";
import { productInfoCardTokens } from "./ProductInfoCard.tokens";

export type ProductInfoCardStyleOptions = {
  ctaColor: string;
};

export const createProductInfoCardStyles = ({
  ctaColor,
}: ProductInfoCardStyleOptions): Record<string, CSSProperties> => {
  return {
    root: {
      width: "100%",
      background: productInfoCardTokens.colors.background,
      color: productInfoCardTokens.colors.text,
      border: `1px solid ${productInfoCardTokens.colors.border}`,
      borderRadius: productInfoCardTokens.radius.outer,
      boxShadow: productInfoCardTokens.colors.shadow,
      fontFamily: productInfoCardTokens.typography.fontFamily,
      overflow: "hidden",
      boxSizing: "border-box",
    },
    layout: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: 20,
      padding: productInfoCardTokens.spacing.outer,
      boxSizing: "border-box",
      alignItems: "start",
    },
    main: {
      display: "grid",
      gap: 18,
      minWidth: 0,
    },
    topGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
      gap: 28,
      alignItems: "start",
    },
    infoColumn: {
      display: "grid",
      gap: 16,
      minWidth: 0,
      alignContent: "start",
    },
    mediaColumn: {
      display: "grid",
      gap: 14,
      minWidth: 0,
    },
    header: {
      display: "grid",
      gap: productInfoCardTokens.spacing.gapTiny,
    },
    eyebrow: {
      display: "inline-flex",
      width: "fit-content",
      alignItems: "center",
      justifyContent: "center",
      padding: "6px 11px",
      borderRadius: productInfoCardTokens.radius.pill,
      border: `1px solid ${productInfoCardTokens.colors.borderStrong}`,
      background: "rgba(17, 17, 17, 0.03)",
      color: productInfoCardTokens.colors.textMuted,
      ...productInfoCardTokens.typography.eyebrow,
    },
    title: {
      margin: 0,
      color: productInfoCardTokens.colors.text,
      ...productInfoCardTokens.typography.title,
    },
    subtitle: {
      margin: 0,
      color: "rgba(17, 17, 17, 0.68)",
      ...productInfoCardTokens.typography.subtitle,
    },
    heroWrap: {
      position: "relative",
      width: "100%",
      overflow: "hidden",
      borderRadius: 22,
      background: "#f5f5f5",
      border: `1px solid ${productInfoCardTokens.colors.border}`,
    },
    heroImage: {
      display: "block",
      width: "100%",
      height: "auto",
      aspectRatio: "16 / 10",
      objectFit: "cover",
    },
    priceBlock: {
      display: "grid",
      gap: 10,
      padding: "0 0 14px 0",
      borderBottom: `1px solid ${productInfoCardTokens.colors.border}`,
    },
    pricingList: {
      display: "grid",
      gap: 10,
    },
    pricingRow: {
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: 16,
    },
    pricingLabel: {
      color: productInfoCardTokens.colors.textMuted,
      ...productInfoCardTokens.typography.label,
    },
    pricingValue: {
      color: productInfoCardTokens.colors.text,
      textAlign: "right",
      ...productInfoCardTokens.typography.price,
    },
    pricingValueNoPrice: {
      color: productInfoCardTokens.colors.textMuted,
      textAlign: "right",
      fontSize: 15,
      lineHeight: 1.35,
      fontWeight: 600,
    },
    noPriceWrap: {
      display: "grid",
      gap: 6,
      justifyItems: "end",
    },
    subPricingList: {
      display: "grid",
      gap: 6,
      marginTop: 4,
    },
    subPricingRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },
    subPricingLabel: {
      color: productInfoCardTokens.colors.textMuted,
      fontSize: 13,
      lineHeight: 1.4,
      fontWeight: 500,
    },
    subPricingValue: {
      color: productInfoCardTokens.colors.text,
      fontSize: 15,
      lineHeight: 1.35,
      fontWeight: 600,
    },
    ctaRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
    },
    galleryGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: 10,
    },
    galleryTileBase: {
      display: "block",
      width: "100%",
      padding: 0,
      margin: 0,
      background: productInfoCardTokens.colors.panelSubtle,
      border: `1px solid ${productInfoCardTokens.colors.border}`,
      borderRadius: productInfoCardTokens.radius.tile,
      overflow: "hidden",
      cursor: "pointer",
      appearance: "none",
      transition:
        "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
    },
    galleryImage: {
      display: "block",
      width: "100%",
      height: "auto",
      aspectRatio: "1 / 1",
      objectFit: "cover",
    },
    contentBlock: {
      display: "grid",
      gap: 12,
    },
    sectionTitle: {
      margin: 0,
      color: productInfoCardTokens.colors.text,
      ...productInfoCardTokens.typography.sectionTitle,
    },
    body: {
      margin: 0,
      color: productInfoCardTokens.colors.textSoft,
      ...productInfoCardTokens.typography.body,
    },
    featureList: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
    },
    featureChip: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 34,
      padding: "7px 12px",
      borderRadius: productInfoCardTokens.radius.pill,
      border: `1px solid ${productInfoCardTokens.colors.borderStrong}`,
      background: "#f8f8f8",
      color: productInfoCardTokens.colors.text,
      fontSize: 13,
      lineHeight: 1.2,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    dimensionGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
      gap: 10,
    },
    dimensionItem: {
      display: "grid",
      gap: 6,
      padding: 14,
      background: "#fafafa",
      borderRadius: 16,
      border: `1px solid ${productInfoCardTokens.colors.border}`,
    },
    dimensionLabel: {
      color: productInfoCardTokens.colors.textFaint,
      ...productInfoCardTokens.typography.xsmall,
    },
    dimensionValue: {
      color: productInfoCardTokens.colors.text,
      fontSize: 15,
      lineHeight: 1.35,
      fontWeight: 700,
    },
    enquirySection: {
      display: "grid",
      gap: 12,
      padding: 16,
      borderRadius: 18,
      border: `1px solid ${productInfoCardTokens.colors.border}`,
      background: productInfoCardTokens.colors.panelSubtle,
    },
    enquiryHeader: {
      display: "grid",
      gap: 4,
    },
    shareSection: {
      display: "grid",
      gridTemplateColumns: "120px minmax(0, 1fr)",
      gap: 16,
      alignItems: "center",
      paddingTop: 4,
      borderTop: `1px solid ${productInfoCardTokens.colors.border}`,
    },
    shareQrWrap: {
      padding: 8,
      borderRadius: 14,
      background: "#fcfcfc",
      border: `1px solid ${productInfoCardTokens.colors.border}`,
    },
    shareContent: {
      display: "grid",
      gap: 10,
      minWidth: 0,
    },
    shareHeader: {
      display: "grid",
      gap: 4,
    },
    shareTitle: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      margin: 0,
      color: productInfoCardTokens.colors.text,
      fontSize: 15,
      lineHeight: 1.3,
      fontWeight: 700,
    },
    shareText: {
      margin: 0,
      color: productInfoCardTokens.colors.textMuted,
      fontSize: 13,
      lineHeight: 1.5,
      fontWeight: 400,
    },
    shareActions: {
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
    },
    buttonBase: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      minHeight: 42,
      padding: "10px 14px",
      borderRadius: 14,
      textDecoration: "none",
      fontSize: 13,
      lineHeight: 1.2,
      fontWeight: 700,
      fontFamily: productInfoCardTokens.typography.fontFamily,
      cursor: "pointer",
      boxSizing: "border-box",
    },
    buttonPrimary: {
      border: `1px solid ${productInfoCardTokens.colors.inverseBg}`,
      background: productInfoCardTokens.colors.inverseBg,
      color: productInfoCardTokens.colors.inverseText,
    },
    buttonCta: {
      border: `1px solid ${ctaColor}`,
      background: ctaColor,
      color: productInfoCardTokens.colors.primaryCtaText,
    },
    buttonSecondary: {
      border: `1px solid ${productInfoCardTokens.colors.borderStrong}`,
      background: productInfoCardTokens.colors.background,
      color: productInfoCardTokens.colors.text,
    },
    buttonSecondaryDisabled: {
      border: `1px solid ${productInfoCardTokens.colors.border}`,
      background: "rgba(17,17,17,0.04)",
      color: "rgba(17,17,17,0.35)",
      pointerEvents: "none",
    },
    buttonTertiary: {
      border: `1px solid ${productInfoCardTokens.colors.borderStrong}`,
      background: "#f7f7f7",
      color: productInfoCardTokens.colors.text,
    },
    buttonNoPriceCta: {
      border: `1px solid ${productInfoCardTokens.colors.borderStrong}`,
      background: productInfoCardTokens.colors.background,
      color: productInfoCardTokens.colors.text,
    },
    qrLink: {
      wordBreak: "break-all",
      color: "rgba(17, 17, 17, 0.52)",
      fontSize: 11,
      lineHeight: 1.45,
      fontWeight: 500,
    },
    rawLinkRow: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      color: productInfoCardTokens.colors.textMuted,
      fontSize: 12,
      lineHeight: 1.4,
      fontWeight: 600,
    },
    emptyState: {
      padding: 14,
      borderRadius: 16,
      border: `1px dashed ${productInfoCardTokens.colors.borderStrong}`,
      background: productInfoCardTokens.colors.background,
      color: "rgba(17, 17, 17, 0.50)",
      fontSize: 13,
      lineHeight: 1.5,
    },
    debugPanel: {
      display: "grid",
      gap: 14,
      padding: 18,
      borderRadius: 20,
      border: `1px solid ${productInfoCardTokens.colors.debugPanelBorder}`,
      background: productInfoCardTokens.colors.debugPanel,
    },
    debugGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 12,
    },
    debugFull: {
      gridColumn: "1 / -1",
    },
    debugGroup: {
      display: "grid",
      gap: 8,
    },
    debugRow: {
      display: "grid",
      gap: 8,
      padding: 12,
      borderRadius: 14,
      background: "#ffffff",
      border: `1px solid ${productInfoCardTokens.colors.border}`,
    },
    debugLabel: {
      fontSize: 12,
      lineHeight: 1.3,
      fontWeight: 700,
      color: productInfoCardTokens.colors.textMuted,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    },
    debugInput: {
      width: "100%",
      minHeight: 42,
      padding: "10px 12px",
      borderRadius: 12,
      border: `1px solid ${productInfoCardTokens.colors.borderStrong}`,
      background: "#ffffff",
      color: productInfoCardTokens.colors.text,
      fontFamily: productInfoCardTokens.typography.fontFamily,
      fontSize: 14,
      lineHeight: 1.4,
      boxSizing: "border-box",
    },
    debugTextarea: {
      width: "100%",
      minHeight: 110,
      padding: "10px 12px",
      borderRadius: 12,
      border: `1px solid ${productInfoCardTokens.colors.borderStrong}`,
      background: "#ffffff",
      color: productInfoCardTokens.colors.text,
      fontFamily: productInfoCardTokens.typography.fontFamily,
      fontSize: 14,
      lineHeight: 1.5,
      boxSizing: "border-box",
      resize: "vertical",
    },
    debugActions: {
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
    },
    debugJsonOutput: {
      width: "100%",
      minHeight: 260,
      padding: 14,
      borderRadius: 16,
      border: `1px solid ${productInfoCardTokens.colors.borderStrong}`,
      background: productInfoCardTokens.colors.debugCodeBg,
      color: productInfoCardTokens.colors.debugCodeText,
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: 13,
      lineHeight: 1.5,
      boxSizing: "border-box",
      resize: "vertical",
    },
    helperText: {
      margin: 0,
      color: productInfoCardTokens.colors.textMuted,
      fontSize: 13,
      lineHeight: 1.5,
    },
    tabBar: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: 8,
      borderRadius: 16,
      border: `1px solid ${productInfoCardTokens.colors.border}`,
      background: "#f8f8f8",
      width: "fit-content",
    },
    tabButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 38,
      padding: "8px 14px",
      borderRadius: 12,
      border: "1px solid transparent",
      background: "transparent",
      color: productInfoCardTokens.colors.textMuted,
      fontSize: 13,
      lineHeight: 1.2,
      fontWeight: 700,
      fontFamily: productInfoCardTokens.typography.fontFamily,
      cursor: "pointer",
      boxSizing: "border-box",
    },
    tabButtonActive: {
      background: productInfoCardTokens.colors.background,
      color: productInfoCardTokens.colors.text,
      border: `1px solid ${productInfoCardTokens.colors.borderStrong}`,
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    },
  };
};
